import { Calendar, Download, FileText, Users } from 'lucide-react';

type FileData = {
  selectedFiles: Array<{
    key?: string;
    name?: string;
    path?: string;
    type?: string;
    field?: string;
    description?: string;
  }>;
  description?: string;
};

interface Staff {
  id: string;
  name: string;
  title: string;
  duties: string;
  noOfServiceYears: string;
}

interface CapacityReviewProps {
  data: {
    f1: FileData;
    f2: FileData;
    f3: FileData;
    f4: FileData;
    f5: FileData;
    f6: FileData;
    staffs: Staff[];
    fiscalYear: string;
    hasCompliance: string;
  };
  onDownload?: (key: string) => void;
}

function isFileData(value: any): value is FileData {
  return typeof value === 'object' && value !== null && 'selectedFiles' in value;
}

export default function CapacityReview({ data, onDownload }: CapacityReviewProps) {
  if (!data) return null;

  // Filter and cast the entries to ensure they are FileData type
  const documentFields = Object.entries(data).filter(([key, value]) => key.startsWith('f') && isFileData(value));

  return (
    <section aria-labelledby='capacity-review-title' className='mt-8'>
      <div className='bg-white sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-gray-900'>
            <div className='flex items-center'>
              <Users className='w-6 h-6 text-gray-900 mr-2' />
              <h2 id='capacity-review-title' className='m-0 text-lg leading-6 font-medium text-gray-900'>
                Step 5: Capacity Review
              </h2>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>Documents</h3>
          <div className='space-y-3'>
            {documentFields.map(([key, value]) => {
              const fileData = value as FileData;
              const fileKey = fileData.selectedFiles?.[0]?.key;
              return (
                fileKey && (
                  <div key={key} className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
                    <div className='flex items-center space-x-4'>
                      <div className='flex items-center'>
                        <FileText className='w-5 h-5 text-gray-900 mr-2' />
                        <span className='text-sm text-gray-900'>Document {key.toUpperCase()}</span>
                      </div>
                      {fileData.description && <span className='text-sm text-gray-500'>Description: {fileData.description}</span>}
                    </div>
                    {onDownload && (
                      <button
                        onClick={() => onDownload(fileKey)}
                        className='flex items-center px-3 py-1 text-sm text-gray-700 hover:text-gray-900'
                      >
                        <Download className='w-4 h-4 mr-1' />
                        Download
                      </button>
                    )}
                  </div>
                )
              );
            })}
          </div>
        </div>

        {/* Staff Section */}
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>Staff Members</h3>
          <div className='space-y-4'>
            {data.staffs.length > 0 ? (
              data.staffs.map((staff) => (
                <div key={staff.id} className='bg-gray-50 p-4 rounded-lg'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <span className='text-gray-500'>Name:</span>
                      <span className='ml-2'>{staff.name}</span>
                    </div>
                    <div>
                      <span className='text-gray-500'>Title:</span>
                      <span className='ml-2'>{staff.title}</span>
                    </div>
                    <div>
                      <span className='text-gray-500'>Years of Service:</span>
                      <span className='ml-2'>{staff.noOfServiceYears}</span>
                    </div>
                    <div>
                      <span className='text-gray-500'>Duties:</span>
                      <span className='ml-2'>{staff.duties}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className='bg-gray-50 p-4 rounded-lg'>
                <span className='text-gray-500'>No staff members listed</span>
              </div>
            )}
          </div>
        </div>

        {/* Additional Details */}
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>Additional Details</h3>
          <div className='space-y-4'>
            <div className='bg-gray-50 p-4 rounded-lg'>
              <div className='flex items-center'>
                <Calendar className='w-5 h-5 text-gray-900 mr-2' />
                <span className='text-gray-500'>Fiscal Year:</span>
                <span className='ml-2'>{data.fiscalYear || 'N/A'}</span>
              </div>
            </div>
            <div className='bg-gray-50 p-4 rounded-lg'>
              <span className='text-gray-500'>Compliance Status:</span>
              <span className='ml-2'>{data.hasCompliance || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
